import { useState, useCallback } from 'react';
import { authClient } from '@/lib/auth-client';
import { Input } from '@sharpe/ui/catalyst';
import { trpc } from '@/lib/trpc';
import { ArrowRight, Email, Checkmark } from '@carbon/icons-react';
import { Button } from '@sharpe/ui/catalyst';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

type FormState = 'idle' | 'loading' | 'sent' | 'error';

interface MagicLinkFormProps {
  mode: 'signin' | 'signup';
  initialEmail?: string;
}

export function MagicLinkForm({ mode, initialEmail = '' }: MagicLinkFormProps) {
  const [email, setEmail] = useState(initialEmail);
  const [name, setName] = useState('');
  const [state, setState] = useState<FormState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [submittedEmail, setSubmittedEmail] = useState('');
  const navigate = useNavigate();

  const signupMutation = trpc.auth.signup.useMutation();
  const checkUserExistsMutation = trpc.auth.checkUserExists.useMutation();

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const trimmedEmail = email.trim().toLowerCase();
      const trimmedName = name.trim();

      if (!trimmedEmail) return;
      if (mode === 'signup' && !trimmedName) return;

      setState('loading');
      setError(null);

      try {
        if (mode === 'signup' && trimmedName) {
          await signupMutation.mutateAsync({
            email: trimmedEmail,
            name: trimmedName,
          });
        } else {
          const { exists, waitlistApproved } = await checkUserExistsMutation.mutateAsync({
            email: trimmedEmail,
          });

          if (!exists) {
            const params = new URLSearchParams({ email: trimmedEmail });
            if (waitlistApproved) params.set('waitlist', 'approved');
            navigate(`/auth/signup?${params.toString()}`);
            return;
          }

          const result = await authClient.signIn.magicLink({
            email: trimmedEmail,
            callbackURL: '/',
          });

          if (result.error) {
            setState('error');
            setError(result.error.message || 'Failed to send magic link. Please try again.');
            return;
          }
        }

        setSubmittedEmail(trimmedEmail);
        setState('sent');
      } catch (err) {
        setState('error');
        const message =
          err instanceof Error ? err.message : 'An unexpected error occurred. Please try again.';
        setError(message);
      }
    },
    [email, name, mode, signupMutation, checkUserExistsMutation, navigate]
  );

  const handleReset = useCallback(() => {
    setState('idle');
    setEmail('');
    setName('');
    setError(null);
  }, []);

  const isDev = import.meta.env.DEV;
  const isSignUp = mode === 'signup';

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.1,
      },
    },
    exit: {
      opacity: 0,
      y: -10,
      transition: { duration: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 12 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring' as const,
        stiffness: 300,
        damping: 24,
      },
    },
  };

  const successVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        type: 'spring' as const,
        stiffness: 300,
        damping: 25,
        staggerChildren: 0.1,
        delayChildren: 0.15,
      },
    },
    exit: {
      opacity: 0,
      scale: 0.95,
      transition: { duration: 0.15 },
    },
  };

  return (
    <AnimatePresence mode="wait">
      {state === 'sent' ? (
        <motion.div
          key="success"
          variants={successVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          className="space-y-5"
        >
          <motion.div
            variants={itemVariants}
            className="flex flex-col items-center text-center py-2"
          >
            <motion.div
              className="relative mb-5"
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <Email size={40} className="text-muted-foreground" />
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.35, type: 'spring', stiffness: 500, damping: 15 }}
                className="absolute -top-1 -right-2 w-5 h-5 rounded-full bg-success flex items-center justify-center shadow-sm"
              >
                <Checkmark size={12} className="text-white dark:text-black" />
              </motion.div>
            </motion.div>

            <motion.p variants={itemVariants} className="text-base font-medium text-foreground">
              {isDev ? 'Check your console' : 'Check your email'}
            </motion.p>
            <motion.p variants={itemVariants} className="text-sm text-muted-foreground mt-1.5">
              We sent a sign-in link to{' '}
              <span className="font-medium text-foreground">{submittedEmail}</span>
            </motion.p>
            <motion.p variants={itemVariants} className="text-xs text-muted-foreground/60 mt-2">
              Expires in 30 minutes. Check spam if you don't see it.
            </motion.p>
          </motion.div>

          <motion.div variants={itemVariants}>
            <Button outline onClick={handleReset} className="w-full">
              Use a different email
            </Button>
          </motion.div>
        </motion.div>
      ) : (
        <motion.form
          key="form"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          onSubmit={handleSubmit}
          className="space-y-3"
        >
          {isSignUp && (
            <motion.div variants={itemVariants}>
              <label className="block text-xs font-medium text-foreground/80 mb-1.5">Name</label>
              <Input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                disabled={state === 'loading'}
                autoComplete="name"
                required
              />
            </motion.div>
          )}

          <motion.div variants={itemVariants}>
            {isSignUp && (
              <label className="block text-xs font-medium text-foreground/80 mb-1.5">Email</label>
            )}
            <div className="flex gap-2">
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={'you@example.com'}
                disabled={state === 'loading'}
                autoComplete="email"
                inputMode="email"
                required
                className="flex-1"
              />
              <motion.button
                type="submit"
                disabled={state === 'loading'}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                transition={{ type: 'spring', stiffness: 400, damping: 17 }}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-ring/30 bg-background dark:bg-sidebar text-foreground transition-colors hover:bg-background/80 dark:hover:bg-sidebar/80 disabled:opacity-50 disabled:pointer-events-none"
              >
                {state === 'loading' ? (
                  <motion.span
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                    className="block h-4 w-4 rounded-full border-2 border-foreground/20 border-t-foreground"
                  />
                ) : (
                  <ArrowRight size={18} />
                )}
              </motion.button>
            </div>
          </motion.div>

          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, height: 0, marginTop: 0 }}
                animate={{ opacity: 1, height: 'auto', marginTop: 12 }}
                exit={{ opacity: 0, height: 0, marginTop: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="rounded-md border border-ds-red-400 bg-ds-red-100 px-3 py-2 text-xs text-destructive">
                  {error}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.form>
      )}
    </AnimatePresence>
  );
}
